import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useScrollPosition from '../../hooks/useScrollPosition.js';
import useWindowWidth from '../../hooks/useWindowWidth.js';
import { setScroll } from '../../redux/scrollSlice.js';
import { useDispatch, useSelector } from 'react-redux';

import luganeseIcon from '../../static/images/region_icons/luganese-icon.png';
import mendrisiottoIcon from '../../static/images/region_icons/mendrisiotto-icon.png';
import bellinzoneseIcon from '../../static/images/region_icons/bellinzonese-icon.png';
import locarneseIcon from '../../static/images/region_icons/locarnese-icon.png';

import { setNewFilters } from '../../redux/filtersSlice.js';
import badger from '../../static/images/badger.png';
import { setIsModalOpen, setSectionOpen } from '../../redux/searchbarSlice.js';
import { useTranslation } from 'react-i18next';

const SearchBar = () => {

    const { t } = useTranslation();

    // Monitoro lo status della SearchBar memorizzato in Redux
    // Lo status gestisce i booleani di visibilità, apertura, chiusura eccetera, non gestisce i dati della SearchBar
    const { isModalOpen, sectionOpen, isAllFilters } = useSelector(state => state.searchbar.modal);
    const { whichPage, isFixed } = useSelector(state => state.searchbar);

    // Gestisco la visualizzazione della searchBar
    const { isPoolVisible } = useSelector(state => state.searchbar)
    const scrollPosition = useScrollPosition();
    const windowWidth = useWindowWidth();


    // Check su filtersObject (corrisponde sempre all'oggetto che viene spedito in backend)
    const { filtersObject } = useSelector(state => state.filters)

    const initialState = {
        where: filtersObject.region || null,
        who: filtersObject.groupModel || null,
        when: filtersObject.availablePeriod || null
    }
    const [navbarFilters, setNavbarFilters] = useState(initialState);
    // Gestisco il cambio di navbarFilters nel caso cambi filtersObject
    useEffect(() => {
        if (filtersObject.region !== navbarFilters.where) setNavbarFilters(prevState => ({ ...prevState, where: filtersObject.region }))
        if (filtersObject.groupModel !== navbarFilters.who) setNavbarFilters(prevState => ({ ...prevState, who: filtersObject.groupModel }))
        if (filtersObject.availablePeriod !== navbarFilters.when) setNavbarFilters(prevState => ({ ...prevState, when: filtersObject.availablePeriod }))
    }, [filtersObject])

    // Gestisco il click sulle tre voci
    const dispatch = useDispatch();
    const handleFiltersClick = (string) => {
        dispatch(setIsModalOpen(true));
        dispatch(setSectionOpen(string));
    }

    // Gestisco lo svuotamento di un filter singolo
    const emptyFilter = (string) => {
        setNavbarFilters(prevState => ({
            ...prevState,
            [string]: null
        }))
    }

    // Gestisco lo stop dello scroll
    useEffect(() => {
        if (windowWidth < 768 && isModalOpen) {
            dispatch(setScroll(false))
        }
    }, [windowWidth, isModalOpen])

    // Gestisco la chiusura della modale
    const handleCloseModal = () => {
        if (windowWidth < 768) {
            dispatch(setSectionOpen(null));
            dispatch(setIsModalOpen(false));
            dispatch(setScroll(true));
        } else {
            dispatch(setIsModalOpen(false));
            dispatch(setSectionOpen(null))
        }
    }

    // Gestisco la scelta del where
    const handleRegionClick = (region) => {
        setNavbarFilters(prevState => ({
            ...prevState,
            where: [region]
        }));
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco la scelta del who
    const handlePeopleClick = (people) => {
        setNavbarFilters(prevState => ({
            ...prevState,
            who: [people]
        }));
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco la scelta del when
    const handleMonthClick = (month) => {
        setNavbarFilters(prevState => ({
            ...prevState,
            when: [month]
        }));
        dispatch(setSectionOpen(null))
        if (windowWidth > 768) {
            dispatch(setIsModalOpen(false))
        }
    }

    // Gestisco il button finale
    const navigate = useNavigate();
    const goToActivities = () => {
        dispatch(setIsModalOpen(false))
        let newFilters = {
            region: navbarFilters.where,
            groupModel: navbarFilters.who,
            availablePeriod: navbarFilters.when
        }
        dispatch(setNewFilters(newFilters))
        dispatch(setScroll(true))
        dispatch(setSectionOpen(null))
        if (whichPage === 'HomePage') navigate("/all-activities");
    }

    useEffect(() => {
        // Questo useEffect rimanda ad All-Activities in automatico dopo una selezione in modalità desktop
        // dato che manca il button
        if ((whichPage === 'HomePage' && (navbarFilters.where || navbarFilters.who || navbarFilters.when)) || whichPage === 'AllActivities') {
            if (windowWidth > 768) {
                goToActivities()
            }
        }
    }, [navbarFilters])

    useEffect(() => {
        setNavbarFilters(initialState)
    }, [])

    return (
        <div className="w-full flex flex-col items-center gap-2 px-4">
            <div className={`bg-white rounded-xl text-neutral-400 w-full md:w-[600px] flex gap-4 flex-col items-center md:flex-row shadow-2xl p-2 ${whichPage === 'HomePage' && isFixed && 'mt-4'}`}>
                <div className={`w-full flex justify-center items-center ${!navbarFilters.where || !navbarFilters.who || !navbarFilters.when ? 'gap-6' : ''} ${!isFixed && 'md:justify-around'}`}>

                    {/* Dove */}
                    <div className="flex items-center">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer items-center hover:bg-[#e2eeed] ${sectionOpen === 'where' && 'bg-[#e2eeed]'}`}>
                            {
                                navbarFilters.where ? (
                                    <div className='text-black text-xs border py-1 px-3 rounded font-bold whitespace-nowrap flex gap-1 items-center'>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => emptyFilter("where")}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div onClick={() => handleFiltersClick('where')}>{navbarFilters.where}</div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('where')}>{t('poolBar.SearchBar.where')}</div>
                                )
                            }
                        </div>
                    </div>
                    {/* Con chi */}
                    <div className="flex items-center">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer hover:bg-[#e2eeed] ${sectionOpen === "who" && 'bg-[#e2eeed]'}`}>
                            {
                                navbarFilters.who ? (
                                    <div className='text-black text-xs border py-1 px-3 rounded font-bold whitespace-nowrap flex gap-1 items-center'>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => emptyFilter("who")}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div onClick={() => handleFiltersClick('who')}>{navbarFilters.who}</div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('who')}>{t('poolBar.SearchBar.who')}</div>
                                )
                            }
                        </div>
                    </div>

                    {/* Quando */}
                    <div className="flex items-center">
                        <div className={`py-3 px-1 md:px-5 rounded-xl cursor-pointer hover:bg-[#e2eeed] ${sectionOpen === "when" && 'bg-[#e2eeed]'}`}>
                            {
                                navbarFilters.when ? (
                                    <div className='text-black text-xs border py-1 px-3 rounded font-bold whitespace-nowrap flex gap-1 items-center'>
                                        {windowWidth > 768 && <div className='pt-1.5' onClick={() => emptyFilter("when")}><i className="fi fi-rr-circle-xmark text-lg"></i></div>}
                                        <div onClick={() => handleFiltersClick('when')}>{navbarFilters.when}</div>
                                    </div>
                                ) : (
                                    <div className='flex whitespace-nowrap text-xs md:text-lg' onClick={() => handleFiltersClick('when')}>{t('poolBar.SearchBar.when')}</div>
                                )
                            }
                        </div>
                    </div>
                </div>


                {/* Button */}
                {/* Al momento in modalità tablet/desktop le chiamate al server vengono effettuate ad ogni cambio di filtri, senza l'utilizzao di button */}
                {/* <div className="hidden md:flex gap-2 items-center ml-4 flex-grow">
                    <button className='bg-[#225424] py-2 px-4 text-white rounded-xl flex gap-2 items-center w-full' onClick={goToActivities}>
                        <i className="fi fi-rr-search mt-1"></i>
                        <div>Cerca</div>
                    </button>
                </div> */}
            </div>

            {/* Open modal */}
            {
                (isModalOpen) &&
                <div className={`fixed z-40 left-2 top-2 right-2 md:w-[600px] bottom-2 md:static md-w-fit flex flex-col gap-2 rounded-xl bg-white`}>
                    <div className={`text-black w-fit rounded-xl cursor-pointer p-2 self-start`} onClick={handleCloseModal}>
                        <i className="fi fi-rr-circle-xmark text-2xl"></i>
                    </div>
                    <div className='flex flex-col justify-center items-center self-center md:hidden'>
                        <img src={badger} alt="Tasso" className='w-48 h-48 m-2 rounded-xl' />
                        <h5 className='text-center leading-none'>{t('poolBar.SearchBar.modalTitle')}</h5>
                    </div>
                    <div className="flex flex-col gap-2 bg-white rounded-xl">
                        <div className='flex-grow flex flex-col pt-2 gap-2 bg-white rounded-xl'>

                            {/* WHERE */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'where' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'where' ? (
                                        <div className='bg-white rounded-xl'>
                                            <h4>{t('poolBar.SearchBar.modalWhereTitle')}</h4>
                                            <div className="flex gap-2 overflow-x-auto whitespace-nowrap hide-scrollbar">
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${navbarFilters.where === 'Ascona-Locarno' && 'bg-[#e2eeed]'}`} src={locarneseIcon} alt="Ascona-Locarno" onClick={() => handleRegionClick('Ascona-Locarno')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${navbarFilters.where === 'Bellinzona e Valli' && 'bg-[#e2eeed]'}`} src={bellinzoneseIcon} alt="Bellinzona e Valli" onClick={() => handleRegionClick('Bellinzona e Valli')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${navbarFilters.where === 'Luganese' && 'bg-[#e2eeed]'}`} src={luganeseIcon} alt="Luganese" onClick={() => handleRegionClick('Luganese')} />
                                                <img className={`border rounded-xl cursor-pointer w-24 hover:bg-[#e2eeed] ${navbarFilters.where === 'Mendrisiotto' && 'bg-[#e2eeed]'}`} src={mendrisiottoIcon} alt="Mendrisiotto" onClick={() => handleRegionClick('Mendrisiotto')} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='cursor-pointer flex'>
                                            {
                                                navbarFilters.where ? (
                                                    <div className="flex w-full justify-between items-center gap-2 text-[#225424]">
                                                        <div className='w-fit rounded-xl cursor-pointer border pt-2 px-2' onClick={() => emptyFilter('where')}>
                                                            <i className="fi fi-rr-circle-xmark text-2xl"></i>
                                                        </div>
                                                        <div className='font-bold border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('where')}>{navbarFilters.where}</div>
                                                    </div>
                                                ) : (
                                                    <div className='border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('where')}>{t('poolBar.SearchBar.modalWhere')}</div>
                                                )
                                            }
                                        </div>
                                    )

                                }
                            </div>

                            {/* WHO */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'who' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'who' ? (
                                        <div>
                                            <h4>{t('poolBar.SearchBar.modalWhoTitle')}</h4>
                                            <div className='flex gap-2 text-[8pt] md:text-sm leading-none text-center overflow-x-auto whitespace-nowrap hide-scrollbar'>
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${navbarFilters.who === 'Da soli' && 'bg-[#e2eeed]'} flex flex-col items-center`} onClick={() => handlePeopleClick('Da soli')}>
                                                    <i className="fi fi-rr-employee-man-alt text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoAlone')}</div>
                                                </div> {/* Alone */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${navbarFilters.who === 'In coppia' && 'bg-[#e2eeed]'} flex flex-col items-center`} onClick={() => handlePeopleClick('In coppia')}>
                                                    <i className="fi fi-rr-people text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoCouple')}</div>
                                                </div> {/* Couple */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${navbarFilters.who === 'Fra amici' && 'bg-[#e2eeed]'} flex flex-col items-center`} onClick={() => handlePeopleClick('Fra amici')}>
                                                    <i className="fi fi-rr-users-alt text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoFriends')}</div>
                                                </div> {/* Friends */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${navbarFilters.who === 'In famiglia' && 'bg-[#e2eeed]'} flex flex-col items-center`} onClick={() => handlePeopleClick('In famiglia')}>
                                                    <i className="fi fi-rr-family text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoFamily')}</div>
                                                </div> {/* Family */}
                                                <div className={`min-w-24 border cursor-pointer hover:bg-[#e2eeed] px-3 py-4 rounded-xl ${navbarFilters.who === 'Per bambini' && 'bg-[#e2eeed]'} flex flex-col items-center`} onClick={() => handlePeopleClick('Per bambini')}>
                                                    <i className="fi fi-rr-man-head text-3xl md:text-5xl"></i>
                                                    <div>{t('poolBar.SearchBar.modalWhoKids')}</div>
                                                </div> {/* Kids */}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='cursor-pointer flex'>
                                            {
                                                navbarFilters.who ? (
                                                    <div className="flex w-full justify-between items-center gap-2 text-[#225424]">
                                                        <div className='w-fit rounded-xl cursor-pointer border pt-2 px-2' onClick={() => emptyFilter('who')}>
                                                            <i className="fi fi-rr-circle-xmark text-2xl"></i>
                                                        </div>
                                                        <div className='font-bold border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('who')}>{navbarFilters.who}</div>
                                                    </div>
                                                ) : (
                                                    <div className='border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('who')}>{t('poolBar.SearchBar.modalWho')}</div>
                                                )
                                            }
                                        </div>
                                    )

                                }
                            </div>

                            {/* WHEN */}
                            <div className={`bg-white rounded-xl p-2 text-black ${isModalOpen && sectionOpen !== 'when' && 'md:hidden'}`}>
                                {
                                    sectionOpen === 'when' ? (
                                        <div>
                                            <h4>{t('poolBar.SearchBar.modalWhenTitle')}</h4>
                                            <div className='grid grid-cols-3'>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Gennaio' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Gennaio')}>{t('poolBar.SearchBar.modalMonths.january')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Febbraio' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Febbraio')}>{t('poolBar.SearchBar.modalMonths.febrary')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Marzo' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Marzo')}>{t('poolBar.SearchBar.modalMonths.march')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Aprile' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Aprile')}>{t('poolBar.SearchBar.modalMonths.april')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Maggio' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Maggio')}>{t('poolBar.SearchBar.modalMonths.may')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Giugno' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Giugno')}>{t('poolBar.SearchBar.modalMonths.june')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Luglio' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Luglio')}>{t('poolBar.SearchBar.modalMonths.july')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Agosto' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Agosto')}>{t('poolBar.SearchBar.modalMonths.august')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Settembre' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Settembre')}>{t('poolBar.SearchBar.modalMonths.september')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Ottobre' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Ottobre')}>{t('poolBar.SearchBar.modalMonths.october')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Novembre' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Novembre')}>{t('poolBar.SearchBar.modalMonths.november')}</div>
                                                <div className={`cursor-pointer hover:bg-[#e2eeed] p-2 rounded-xl ${navbarFilters.when === 'Dicembre' && 'bg-[#e2eeed]'}`} onClick={() => handleMonthClick('Dicembre')}>{t('poolBar.SearchBar.modalMonths.december')}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='cursor-pointer flex'>
                                            {
                                                navbarFilters.when ? (
                                                    <div className="flex w-full justify-between items-center gap-2 text-[#225424]">
                                                        <div className='w-fit rounded-xl cursor-pointer border pt-2 px-2' onClick={() => emptyFilter('when')}>
                                                            <i className="fi fi-rr-circle-xmark text-2xl"></i>
                                                        </div>
                                                        <div className='font-bold border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('when')}>{navbarFilters.when}</div>
                                                    </div>
                                                ) : (
                                                    <div className='border p-2 rounded-xl flex-grow' onClick={() => handleFiltersClick('when')}>{t('poolBar.SearchBar.modalWhen')}</div>
                                                )
                                            }
                                        </div>
                                    )

                                }
                            </div>

                            {/* Button */}
                            <button className='bg-[#225424] ml-2 my-2 py-2 px-4 text-white rounded-xl flex gap-2 items-center md:hidden' onClick={goToActivities}>
                                <i className="fi fi-rr-search mt-1"></i>
                                <div>{t('poolBar.SearchBar.modalButton')}</div>
                            </button>
                        </div>
                    </div>
                </div>
            }

        </div >

    )
}

export default SearchBar;